import React from 'react'
import { useList } from '@refinedev/core'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, Pill, EmptyHint, TableSurface, type Stat } from '../../theme/listkit'
import { Medal, UsersRound } from '../../components/icons'

interface MemberBadge {
  id: string
  user_id: string
  badge_key: string
  earned_at: string
}

interface MemberProfile {
  user_id: string
  name: string | null
  class_year: string | null
}

interface BadgesSummary {
  total_badges: number | null
  distinct_badges: number | null
  distinct_members: number | null
}

const fmtWhen = (v: string | null) =>
  v
    ? new Date(v).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—'

/**
 * Member Badges — read-only view of the engagement badges members have earned
 * (member_badges table), newest first. A user_id alone doesn't say WHO earned a
 * badge, so each row is enriched with the member's name + class year from a
 * second query against `profiles` (filtered to just the user_ids on screen). The
 * raw user_id is kept as a small caption for reference. Both lists use liveMode
 * 'off' (the only data sources here) to avoid double subscriptions.
 */
export function MemberBadgesList() {
  // Primary list: a BOUNDED page of the most-recent badges (was mode:'off').
  const { result } = useList<MemberBadge>({
    resource: 'member_badges',
    pagination: { pageSize: 200 },
    liveMode: 'off',
    sorters: [{ field: 'earned_at', order: 'desc' }],
  })
  const rows = result?.data ?? []

  // Stat deck: a single pre-aggregated row from the admin summary VIEW instead of
  // counting/Set-ing over the whole member_badges table on the main thread.
  const { result: summaryRes } = useList<BadgesSummary>({
    resource: 'admin_member_badges_summary',
    pagination: { pageSize: 1 },
    liveMode: 'off',
  })
  const summary = summaryRes?.data?.[0]

  // Resolve who each badge belongs to. Fetch only the profiles referenced by the
  // badges on screen (filtered by user_id IN […]); the query is disabled until
  // there are badges to resolve.
  const userIds = React.useMemo(
    () => Array.from(new Set(rows.map((r) => r.user_id))),
    [rows],
  )
  const { result: profileResult } = useList<MemberProfile>({
    resource: 'profiles',
    pagination: { mode: 'off' },
    liveMode: 'off',
    filters: userIds.length ? [{ field: 'user_id', operator: 'in', value: userIds }] : [],
    queryOptions: { enabled: userIds.length > 0 },
  })
  const profileById = React.useMemo(() => {
    const m = new Map<string, MemberProfile>()
    for (const p of profileResult?.data ?? []) m.set(p.user_id, p)
    return m
  }, [profileResult])

  const totalBadges = summary?.total_badges ?? rows.length
  const uniqueBadges = summary?.distinct_badges ?? new Set(rows.map((r) => r.badge_key)).size
  const uniqueMembers = summary?.distinct_members ?? userIds.length

  const stats: Stat[] = [
    { label: 'Badges Earned', value: totalBadges.toLocaleString(), icon: Medal, tone: 'gold' },
    { label: 'Badge Types', value: uniqueBadges.toLocaleString(), icon: Medal, tone: 'jaguar' },
    { label: 'Members', value: uniqueMembers.toLocaleString(), icon: UsersRound, tone: 'green' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Members · Badges',
        description:
          'Every engagement badge members have earned across the app — newest first. A read-only window into how the community is showing up.',
      }}
      stats={stats}
    >
      <SectionCard title="Member Badges" sub="Earned across the app · newest first">
        {rows.length === 0 ? (
          <EmptyHint>No badges earned yet.</EmptyHint>
        ) : (
          <TableSurface>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Badge', 'Member', 'Class', 'Earned'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '12px 16px',
                        fontFamily: brand.fSans,
                        fontWeight: 700,
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '.05em',
                        color: brand.sub,
                        borderBottom: '1px solid ' + brand.cardLine,
                        background: brand.paper,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((b, i) => {
                  const profile = profileById.get(b.user_id)
                  return (
                    <tr
                      key={b.id}
                      style={{
                        borderBottom: i < rows.length - 1 ? '1px solid ' + brand.cardLine : 'none',
                      }}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <Pill tone="gold">{b.badge_key}</Pill>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div
                          style={{
                            fontFamily: brand.fSans,
                            fontWeight: 600,
                            fontSize: 12.5,
                            color: brand.ink,
                          }}
                        >
                          {profile?.name || 'Unknown member'}
                        </div>
                        <code
                          style={{
                            fontSize: 11,
                            color: brand.sub,
                          }}
                        >
                          {b.user_id}
                        </code>
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          fontFamily: brand.fSans,
                          fontWeight: 500,
                          fontSize: 12.5,
                          color: brand.sub,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {profile?.class_year ? `Class of ${profile.class_year}` : '—'}
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          fontFamily: brand.fSans,
                          fontWeight: 500,
                          fontSize: 12.5,
                          color: brand.sub,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fmtWhen(b.earned_at)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </TableSurface>
        )}
      </SectionCard>
    </ListPage>
  )
}
